/**
 * service-mail cross-dock manifest client. Mirrors:
 *   apps/service-mail/src/modules/cross-dock/cross-dock.controller.ts
 *
 * RBAC:
 *   - createManifest verbs need Mail/createManifest.
 *   - seal/dispatch need Mail/sealManifest.
 *   - receive needs Mail/receiveManifest.
 *   - read endpoints need Mail/readManifest.
 */
import { env } from '../env';

import { request } from './client';

export type ManifestStatus = 'DRAFT' | 'SEALED' | 'IN_TRANSIT' | 'RECEIVED' | 'DISCREPANCY';

export interface ManifestItemDto {
  readonly id: string;
  readonly manifestId: string;
  readonly mailItemId: string;
  readonly trackingNumber: string;
  readonly addedAt: string;
}

export interface ManifestDto {
  readonly id: string;
  readonly manifestNumber: string;
  readonly originOutletCode: string;
  readonly destinationOutletCode: string;
  readonly vehicleRef: string | null;
  readonly driverRef: string | null;
  readonly status: ManifestStatus;
   
  readonly itemCount: number;
   
  readonly totalWeightGrams: number;
  readonly sealedAt: string | null;
  readonly receivedAt: string | null;
  readonly notes: string | null;
  readonly createdBy: string;
  readonly createdAt: string;
  readonly updatedAt: string;
}

export interface ManifestDetailDto extends ManifestDto {
  readonly items: readonly ManifestItemDto[];
}

export interface ListManifestsResponse {
  readonly items: readonly ManifestDto[];
  // eslint-disable-next-line upmp/no-number-money -- row count
  readonly total: number;
  readonly page: number;
  readonly pageSize: number;
}

export interface ListManifestsQuery {
  readonly status?: ManifestStatus;
  readonly origin?: string;
  readonly destination?: string;
  readonly from?: string;
  readonly to?: string;
  readonly page?: number;
  readonly pageSize?: number;
}

export interface CreateManifestBody {
  readonly originOutletCode: string;
  readonly destinationOutletCode: string;
  readonly notes?: string;
}

const baseUrl = (): string => env.serviceMailBaseUrl;

export async function listManifests(query: ListManifestsQuery = {}): Promise<ListManifestsResponse> {
  return request<ListManifestsResponse>(baseUrl(), '/v1/manifests', {
    method: 'GET',
    query: {
      status: query.status,
      origin: query.origin,
      destination: query.destination,
      from: query.from,
      to: query.to,
      page: query.page,
      pageSize: query.pageSize,
    },
  });
}

export async function getManifest(id: string): Promise<ManifestDetailDto> {
  return request<ManifestDetailDto>(baseUrl(), `/v1/manifests/${encodeURIComponent(id)}`, {
    method: 'GET',
  });
}

export async function createManifest(body: CreateManifestBody): Promise<ManifestDto> {
  return request<ManifestDto>(baseUrl(), '/v1/manifests', { method: 'POST', body });
}

export async function addManifestItems(id: string, trackingNumbers: readonly string[]): Promise<ManifestDetailDto> {
  return request<ManifestDetailDto>(
    baseUrl(),
    `/v1/manifests/${encodeURIComponent(id)}/items`,
    { method: 'POST', body: { trackingNumbers } },
  );
}

export async function removeManifestItem(id: string, trackingNumber: string): Promise<ManifestDetailDto> {
  return request<ManifestDetailDto>(
    baseUrl(),
    `/v1/manifests/${encodeURIComponent(id)}/items/${encodeURIComponent(trackingNumber)}`,
    { method: 'DELETE' },
  );
}

export async function sealManifest(
  id: string,
  body: { vehicleRef?: string; driverRef?: string } = {},
): Promise<ManifestDto> {
  return request<ManifestDto>(baseUrl(), `/v1/manifests/${encodeURIComponent(id)}/seal`, {
    method: 'POST',
    body,
  });
}

export async function dispatchManifest(id: string): Promise<ManifestDto> {
  return request<ManifestDto>(baseUrl(), `/v1/manifests/${encodeURIComponent(id)}/dispatch`, {
    method: 'POST',
  });
}

export async function receiveManifest(
  id: string,
  body: { discrepancyTrackingNumbers?: readonly string[] } = {},
): Promise<ManifestDto> {
  return request<ManifestDto>(baseUrl(), `/v1/manifests/${encodeURIComponent(id)}/receive`, {
    method: 'POST',
    body,
  });
}

export function manifestCsvUrl(id: string): string {
  return `${baseUrl()}/v1/manifests/${encodeURIComponent(id)}/manifest.csv`;
}
